// The `ReadingPanes` context — the seam that lets chrome AROUND the panes read
// what the panes resolved without re-deriving it (composition policy §2.2's
// `state`/`actions`/`meta` interface). `ReadingPanes` is the only provider
// today; the coming `/admin` editor workbench renders the same panes with its
// own toolbar inside this provider, so a control does not have to be visually
// nested in the pane row to drive it.
//
// Deliberately NOT a place for persistence: both `setPaneFont` and `setLinked`
// report a new `FontSizePrefs` upward and nothing here writes it. Whoever owns
// the state decides whether it reaches localStorage — the reader persists, the
// workbench must not (an admin sizing the original to read Homer is not
// re-preferencing their own library).

import { createContext, use, type RefObject } from "react";
import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import type {
  FontSizePrefs,
  PaneId,
  SyncMode,
} from "@bindery/shared/corpus/reader";

/** What the panes are currently showing. All derived or passed in — no state. */
export interface ReadingPanesState {
  retellingModel: DocumentModel;
  originalModel: DocumentModel;
  anchors: readonly CorpusAnchorPair[];
  /** The original is open — as a second pane OR woven in. */
  showOriginal: boolean;
  /** One scroll column with the original woven in as collapsible insets. */
  interleaved: boolean;
  /**
   * Two independent scroll panes: the original is open and NOT woven in. The
   * predicate that gates the align/link toggles and both sync hooks — derived
   * as `showOriginal && !interleaved`, which is exactly the reader's old
   * `showOriginal && !isNarrow` (when `showOriginal` holds, `interleaved` IS
   * `isNarrow`; when it doesn't, both are false).
   */
  twoPane: boolean;
  syncMode: SyncMode;
  fontSize: FontSizePrefs;
  /** Non-English source — gates the gloss entry points and the popover. */
  glossEligible: boolean;
}

export interface ReadingPanesActions {
  /**
   * Resize one pane, honouring `linked`. Reports the whole next `FontSizePrefs`
   * to the owner; it does NOT persist (see the file header).
   */
  setPaneFont: (pane: PaneId, value: number) => void;
  /**
   * Tie the two panes' sizes together (or cut them loose). Linking unifies both
   * to the RETELLING's size — the retelling is the pane being read, so it is
   * the one whose size survives the merge. Reported upward exactly like
   * `setPaneFont`, and attributed to the retelling pane, so an owner that logs
   * font changes sees the same shape either way.
   */
  setLinked: (linked: boolean) => void;
}

export interface ReadingPanesMeta {
  retellingRef: RefObject<HTMLDivElement | null>;
  originalRef: RefObject<HTMLDivElement | null>;
  /**
   * Opaque remeasure token the two sync hooks are keyed on. Changing it tears
   * down and re-runs their measurement pass; nothing should parse it.
   */
  layoutKey: string;
}

export interface ReadingPanesContextValue {
  state: ReadingPanesState;
  actions: ReadingPanesActions;
  meta: ReadingPanesMeta;
}

export const ReadingPanesContext =
  createContext<ReadingPanesContextValue | null>(null);

/** Read the pane context. Throws outside a `ReadingPanes`. */
export function useReadingPanes(): ReadingPanesContextValue {
  const ctx = use(ReadingPanesContext);
  if (!ctx) {
    throw new Error("useReadingPanes must be used within a ReadingPanes");
  }
  return ctx;
}

/**
 * Identifies WHICH component renders the retelling column. Lives here rather
 * than beside `ReadingPanes` because the ROUTE needs it too — `useReadingNav`
 * and `useCopyHandler` both take it, and they run ABOVE `ReadingPanes`, not
 * inside it. Deriving it in one place keeps the route's token and the panes'
 * actual structure from drifting apart.
 *
 * Deliberately NARROWER than `layoutKey`: that one also changes on font and
 * sync-mode changes so the panes remeasure, whereas this only has to change
 * when the retelling node is genuinely REPLACED (`InterleavedColumn` and
 * `DocumentPane` occupy the same tree slot, so flipping between them puts a new
 * node under the same ref). Do not merge the two.
 */
export function paneColumnKey(interleaved: boolean): string {
  return interleaved ? "interleaved" : "pane";
}
